import { gql } from '@graphql/__generated__';
import type {
  GetAgentReviewsQuery,
  GetAgentTransactionsQuery,
} from '@graphql/__generated__/graphql';
import { Order_By } from '@graphql/__generated__/graphql';
import type {
  DataTransformer,
  NormalizedReview,
  NormalizedTransaction,
  QueryConfig,
} from '@modules/_shared/components/visibility-drawer/queries';
import { getTwoYearsAgo } from '@modules/_shared/utils/dates';

export const GET_TEAM_MEMBER_EDIT_DETAILS = gql(/* GraphQL */ `
  query GetTeamMemberEditDetails($userId: uuid!) {
    users_by_pk(id: $userId) {
      id
      first_name
      last_name
      description
      facebook_profile
      instagram_profile
      tiktok_profile
      x_profile
      youtube_profile
      linked_in_profile
      services
      certifications
      languages
      hires_on_realadvisor
      active_since
      show_in_agency_pages
      agent_slug
      ...UserActiveModuleGrants
      phone_numbers(order_by: [{ primary: desc }, { created_at: desc }]) {
        id
        number
        primary
        type
        is_visibility
      }
      user_images(where: { is_primary: { _eq: true } }, limit: 1) {
        id
        image {
          id
          url
        }
      }
      teams_users {
        team {
          id
          name
          phone_numbers(order_by: [{ primary: desc }]) {
            id
            number
            type
          }
          cover_photo {
            id
            url
          }
        }
      }
      __typename
    }
  }
`);

export const GET_AGENT_REVIEWS = gql(/* GraphQL */ `
  query GetAgentReviews(
    $userId: uuid!
    $limit: Int!
    $offset: Int!
    $orderBy: [reviews_order_by!]
  ) {
    users_by_pk(id: $userId) {
      id
      reviews(
        where: { completed: { _eq: true } }
        limit: $limit
        offset: $offset
        order_by: $orderBy
      ) {
        id
        rating
        review_text
        completed_at
        display_name
        transaction_id
        owner_answer
        owner_answer_date
      }
    }
  }
`);

export const GET_AGENT_REVIEWS_COUNT = gql(/* GraphQL */ `
  query GetAgentReviewsCount($userId: uuid!) {
    users_by_pk(id: $userId) {
      id
      reviews_aggregate(where: { completed: { _eq: true } }) {
        aggregate {
          count
          avg {
            rating
          }
        }
      }
    }
  }
`);

export const GET_AGENT_TRANSACTIONS = gql(/* GraphQL */ `
  query GetAgentTransactions(
    $userId: uuid!
    $twoYearsAgo: timestamptz!
    $limit: Int!
    $offset: Int!
  ) {
    property_transactions(
      where: {
        seller_broker_id: { _eq: $userId }
        signature_date: { _gte: $twoYearsAgo, _lte: "now()" }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        is_published: { _eq: true }
      }
      order_by: { signature_date: desc }
      limit: $limit
      offset: $offset
    ) {
      id
      property_id
      signature_date
      purchase_price
      property {
        id
        country_code
        property_type {
          label
        }
        number_of_rooms
        living_surface
        built_surface
        route
        street_number
        postcode
        locality_place {
          label
        }
        property_images(limit: 1, order_by: { order_nr: asc }) {
          image {
            url
          }
        }
      }
      reviews_aggregate(where: { completed: { _eq: true } }) {
        aggregate {
          count
        }
      }
      seller_broker {
        id
        first_name
        last_name
      }
      seller_broker_team {
        id
        name
      }
    }
  }
`);

export const GET_AGENT_TRANSACTIONS_COUNT = gql(/* GraphQL */ `
  query GetAgentTransactionsCount($userId: uuid!, $twoYearsAgo: timestamptz!) {
    property_transactions_aggregate(
      where: {
        seller_broker_id: { _eq: $userId }
        signature_date: { _gte: $twoYearsAgo, _lte: "now()" }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        is_published: { _eq: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const agentReviewsQuery: QueryConfig = {
  query: GET_AGENT_REVIEWS,
  getVariables: params => ({
    userId: params.entityId,
    limit: params.limit,
    offset: params.offset,
    orderBy: [{ completed_at: Order_By.Desc }],
  }),
};

export const agentReviewsTransformer: DataTransformer<
  GetAgentReviewsQuery,
  NormalizedReview
> = data => {
  const user = data?.users_by_pk;
  if (!user) {
    return { items: [], totalCount: 0 };
  }

  const reviews = user.reviews ?? [];

  return {
    items: reviews.map(review => ({
      id: review.id,
      rating: review.rating,
      reviewText: review.review_text,
      completedAt: review.completed_at,
      displayName: review.display_name,
      ownerAnswer: review.owner_answer,
      ownerAnswerDate: review.owner_answer_date,
      isVerified: !!review.transaction_id,
    })),
    totalCount: 0, // Will be provided by separate count query
  };
};

export const agentTransactionsQuery: QueryConfig = {
  query: GET_AGENT_TRANSACTIONS,
  getVariables: params => ({
    userId: params.entityId,
    twoYearsAgo: getTwoYearsAgo().toISOString().slice(0, 10),
    limit: params.limit,
    offset: params.offset,
  }),
};

export const agentTransactionsTransformer: DataTransformer<
  GetAgentTransactionsQuery,
  NormalizedTransaction
> = data => {
  const transactions = data?.property_transactions ?? [];

  return {
    items: transactions.map(transaction => ({
      id: transaction.id,
      propertyId: transaction.property_id,
      signatureDate: transaction.signature_date,
      purchasePrice: transaction.purchase_price,
      propertyType: transaction.property?.property_type?.label,
      numberOfRooms: transaction.property?.number_of_rooms,
      locality: transaction.property?.locality_place?.label,
      imageUrl: transaction.property?.property_images?.[0]?.image?.url,
      isVerified: (transaction.reviews_aggregate?.aggregate?.count ?? 0) > 0,
      soldByAgent: [
        transaction.seller_broker?.first_name,
        transaction.seller_broker?.last_name,
      ]
        .filter(Boolean)
        .join(' '),
      soldByTeam: transaction.seller_broker_team?.name ?? '',
    })),
    totalCount: 0, // Will be provided by separate count query
  };
};
